import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const ContactForm = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
}));

const contactStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  url: 'https://www.thranatech.com/contact',
  description: 'Get in touch with Thrana for pharmacovigilance software, services, and training inquiries.',
  publisher: {
    '@type': 'Organization',
    name: 'Thranatech.com',
    url: 'https://www.thranatech.com',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91 755 8838 883',
    contactType: 'customer service',
    email: 'contact@thranatech.com',
  },
};

const Contact = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners = [
    '/banner1.jpg',
    '/banner2.jpg',
    '/healthcare.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Real-time email validation
    if (name === 'email') {
      const emailError = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {
      name: formData.name.trim() ? '' : 'Name is required',
      email: validateEmail(formData.email),
      message: formData.message.trim() ? '' : 'Message is required',
      company: '',
    };

    setErrors(newErrors);

    // Check if there are any errors
    if (newErrors.name || newErrors.email || newErrors.message) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors in the form before submitting.',
        severity: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get EmailJS configuration - supports both .env (development) and public config (production)
      // Priority: 1. Environment variables (.env) 2. Public config file (window.EMAILJS_CONFIG)
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || window.EMAILJS_CONFIG?.SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || window.EMAILJS_CONFIG?.TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || window.EMAILJS_CONFIG?.PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey || 
          serviceId === 'your_service_id_here' || 
          templateId === 'your_template_id_here' || 
          publicKey === 'your_public_key_here') {
        throw new Error('Email service is not configured. Please contact us directly at contact@thranatech.com');
      }

      // Prepare template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company || 'N/A',
        message: formData.message,
        to_email: 'contact@thranatech.com',
        source: 'contact_page',
        type: 'general_inquiry',
        date: new Date().toLocaleString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
      };

      // Send email using EmailJS with timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout. Please check your internet connection and try again.')), 15000)
      );

      await Promise.race([
        emailjs.send(serviceId, templateId, templateParams, publicKey),
        timeoutPromise
      ]);
      
      setSnackbar({
        open: true,
        message: 'Thank you for your message! We will get back to you soon.',
        severity: 'success',
      });
      
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
      });
    } catch (error) {
      console.error('EmailJS error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to send message. Please try again later.';
      
      if (error.text) {
        // EmailJS specific error
        if (error.text.includes('Invalid template ID') || error.text.includes('Template not found')) {
          errorMessage = 'Email service configuration error. Please contact us directly at contact@thranatech.com';
        } else if (error.text.includes('Invalid service ID')) {
          errorMessage = 'Email service configuration error. Please contact us directly at contact@thranatech.com';
        } else if (error.text.includes('Rate limit')) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else {
          errorMessage = error.text;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Add fallback email option in error message
      const fallbackMessage = `${errorMessage} Alternatively, you can email us directly at contact@thranatech.com`;
      
      setSnackbar({
        open: true,
        message: fallbackMessage,
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <SEO
        title="Contact Thrana | Pharmacovigilance Experts"
        description="Connect with Thrana to request demos, discuss pharmacovigilance services, or enroll in training programs."
        path="/contact"
        structuredData={contactStructuredData}
      />
      <Box>
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={index}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h6"
              paragraph
              sx={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                color: 'white',
              }}
            >
              Get in touch with our team
            </Typography>
          </Container>
        </Overlay>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom>
                Contact Information
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ mr: 2 }} />
                  <Typography>
                    <a href="mailto:contact@thranatech.com" style={{ color: 'inherit', textDecoration: 'none' }}>
                      contact@thranatech.com
                    </a>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ mr: 2 }} />
                  <Typography>
                    <a href="tel:+917558838883" style={{ color: 'inherit', textDecoration: 'none' }}>
                      +91 755 8838 883
                    </a>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <LocationOnIcon sx={{ mr: 2, mt: 0.5 }} />
                  <Typography>
                    Thrana Softech Pvt. Ltd.,<br />
                    2nd Floor, Sri Bhavani Arcade,<br />
                    No 7, Annaswamy Mudaliar Rd, <br />
                    Halasuru, Bengaluru-560042, <br />
                    India.
 
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <ContactForm elevation={3}>
              <Typography variant="h5" gutterBottom>
                Send us a Message
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Fill out the form below or email us directly at{' '}
                <a href="mailto:contact@thranatech.com" style={{ color: '#3c005a', textDecoration: 'none' }}>
                  contact@thranatech.com
                </a>
              </Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      error={!!errors.email}
                      helperText={errors.email}
                      inputProps={{
                        pattern: '[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </ContactForm>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </>
  );
};

export default Contact; 